import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetInventoryPlanQueryKey,
  getListInventoryPlansQueryKey,
  getListInventoryPlanPriceHistoryQueryKey,
  useChangeInventoryPlanPrice,
  useListInventoryPlanPriceHistory,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

function inr(n: number): string {
  return `₹${n.toLocaleString("en-IN")}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export interface ChangePriceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: { id: number; planName: string; monthlyPrice: number | string };
  /** Called after a successful change (parent may refetch its own plan view). */
  onChanged?: () => void;
}

/**
 * ChangePriceDialog — the "Change Price" CTA flow.
 *
 * A plan's headline monthly price can only ever be RAISED (server enforces
 * price_cannot_decrease / price_unchanged; the client mirrors the guard so the
 * button is disabled before submit). Each change appends a version-history row,
 * shown newest-first below the form. Running memberships are untouched — they keep
 * their frozen price for the current term and pick up the new price on renewal.
 */
export function ChangePriceDialog({ open, onOpenChange, plan, onChanged }: ChangePriceDialogProps) {
  const currentPrice = Number(plan.monthlyPrice) || 0;
  const [newPriceStr, setNewPriceStr] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const { toast } = useToast();
  const qc = useQueryClient();
  const changePrice = useChangeInventoryPlanPrice();

  // Reset form each time the dialog opens for a plan.
  useEffect(() => {
    if (open) {
      setNewPriceStr("");
      setReason("");
    }
  }, [open, plan.id]);

  const { data: history = [], isLoading: historyLoading } = useListInventoryPlanPriceHistory(
    plan.id,
    { query: { enabled: open, queryKey: getListInventoryPlanPriceHistoryQueryKey(plan.id) } },
  );

  const parsed = newPriceStr.trim() === "" ? null : Number(newPriceStr);
  const validationError = useMemo(() => {
    if (parsed == null) return null; // empty — not an error, just not submittable
    if (!Number.isFinite(parsed) || parsed < 0) return "Enter a valid amount.";
    if (parsed === currentPrice) return `Enter an amount higher than the current ${inr(currentPrice)}.`;
    if (parsed < currentPrice) return `New price can't be lower than the current ${inr(currentPrice)}.`;
    return null;
  }, [parsed, currentPrice]);

  const canSubmit =
    parsed != null && Number.isFinite(parsed) && parsed > currentPrice && !changePrice.isPending;

  async function submit() {
    if (!canSubmit || parsed == null) return;
    try {
      await changePrice.mutateAsync({
        id: plan.id,
        data: { newPrice: parsed, reason: reason.trim() || null },
      });
      qc.invalidateQueries({ queryKey: getListInventoryPlansQueryKey() });
      qc.invalidateQueries({ queryKey: getGetInventoryPlanQueryKey(plan.id) });
      qc.invalidateQueries({ queryKey: getListInventoryPlanPriceHistoryQueryKey(plan.id) });
      toast({
        title: "Price updated",
        description: `${plan.planName.replace(/_/g, " ")} — ${inr(currentPrice)} → ${inr(parsed)}`,
      });
      onChanged?.();
      onOpenChange(false);
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      const msg =
        code === "price_cannot_decrease"
          ? "A plan's price can't be lowered."
          : code === "price_unchanged"
            ? "That's the same as the current price."
            : (err as Error).message ?? "Try again.";
      toast({ title: "Couldn't update price", description: msg, variant: "destructive" });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change price — {plan.planName.replace(/_/g, " ")}</DialogTitle>
          <DialogDescription>
            Raise the plan&apos;s monthly price. Existing customers keep their current
            price until renewal; only new plans use the new price. A price can never be
            lowered.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
            <span className="text-sm text-muted-foreground">Current price</span>
            <span className="font-mono text-sm font-medium">{inr(currentPrice)}</span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-price">New price (₹)</Label>
            <Input
              id="new-price"
              type="number"
              inputMode="decimal"
              min={currentPrice}
              step="0.01"
              placeholder={`Higher than ${inr(currentPrice)}`}
              value={newPriceStr}
              onChange={(e) => setNewPriceStr(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) submit();
              }}
              aria-invalid={validationError != null}
            />
            {validationError && <p className="text-xs text-destructive">{validationError}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="price-reason">Reason (optional)</Label>
            <Textarea
              id="price-reason"
              rows={2}
              placeholder="e.g. Annual revision, cost increase…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Price history</p>
            {historyLoading ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : history.length === 0 ? (
              <p className="text-xs text-muted-foreground">No changes yet.</p>
            ) : (
              <ul className="max-h-40 space-y-1.5 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="rounded-md border px-2.5 py-1.5 text-xs">
                    <div className="flex items-center justify-between font-mono">
                      <span>
                        {inr(Number(h.oldPrice))} → <span className="font-medium">{inr(Number(h.newPrice))}</span>
                      </span>
                      <span className="text-muted-foreground">{formatDate(h.changeDate)}</span>
                    </div>
                    <div className="mt-0.5 text-muted-foreground">
                      {h.changedByName ? `by ${h.changedByName}` : "by —"}
                      {h.reason ? ` · ${h.reason}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={changePrice.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {changePrice.isPending ? "Saving…" : "Save new price"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
